import {
  BUILT_IN_ACCESS_OPTION_ID,
  BUILT_IN_ACCESS_OPTION_LABEL,
} from "@via/pages/data/constants";
import type { SelectOption } from "@via/utils/interfaces";
import type {
  RunSchedulerOverwriteMethod,
  RunSchedulerOverwriteParameter,
  RunSchedulerOverwriteValueType,
  RunSchedulerSamplesheetFileLocation,
  RunSchedulerSourceMethod,
  RunSchedulerSourcePathDerivation,
} from "common";

export const INTERVAL_OPTIONS: SelectOption[] = [
  { label: "15 min", value: 15 },
  { label: "30 min", value: 30 },
  { label: "60 min", value: 60 },
  { label: "120 min", value: 120 },
  { label: "6 hours", value: 360 },
  { label: "12 hours", value: 720 },
  { label: "1 day", value: 1440 },
];

export const DEFAULT_CREDENTIAL_OPTION: SelectOption = {
  label: BUILT_IN_ACCESS_OPTION_LABEL,
  value: BUILT_IN_ACCESS_OPTION_ID,
};

export const SOURCE_METHOD_OPTIONS: Array<{
  label: string;
  value: RunSchedulerSourceMethod;
}> = [
  { label: "Sample Tracker", value: "sample-tracker" },
  { label: "Watch Path", value: "watch-path" },
];

export const OVERWRITE_VALUE_TYPE_OPTIONS: Array<{
  label: string;
  value: RunSchedulerOverwriteValueType;
}> = [
  { label: "Value from tracker field", value: "sample-tracker-field" },
  { label: "Append Relative Path to Source Path", value: "relative-path" },
  {
    label: "Value from JSON file (relative path + key)",
    value: "json-key-from-relative-path",
  },
  { label: "Derive from source path", value: "derive-from-source-path" },
];

export const OVERWRITE_METHOD_OPTIONS: Array<{
  label: string;
  value: RunSchedulerOverwriteMethod;
}> = [
  { label: "Replace", value: "replace" },
  { label: "Append as a suffix", value: "append-suffix" },
  { label: "Save as dataset", value: "save-as-dataset" },
];

export const SAMPLESHEET_FILE_LOCATION_OPTIONS: Array<{
  label: string;
  value: RunSchedulerSamplesheetFileLocation;
}> = [
  { label: "Local Server Disk (Run environment)", value: "run-environment" },
  { label: "Amazon S3 Bucket", value: "s3" },
  { label: "Google GCP Bucket", value: "gcs" },
];

export const SOURCE_PATH_DERIVATION_OPTIONS: Array<{
  label: string;
  value: RunSchedulerSourcePathDerivation;
}> = [
  { label: "Full source path", value: "full" },
  { label: "Final folder name (last segment)", value: "leaf-folder" },
];

export type OverwriteRow = RunSchedulerOverwriteParameter & { id: string };

export function newRowId(): string {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `${Date.now()}-${Math.random().toString(16).slice(2)}`
  );
}

export function createEmptyOverwriteRow(
  sourceMethod: RunSchedulerSourceMethod
): OverwriteRow {
  return {
    id: newRowId(),
    configParameter: "",
    method: "replace",
    valueType:
      sourceMethod === "watch-path"
        ? "derive-from-source-path"
        : "sample-tracker-field",
    sampleTrackerField: "",
    sourcePathDerivation: sourceMethod === "watch-path" ? "full" : undefined,
  };
}

export interface SchedulerFormValues {
  name: string;
  enabled: boolean;
  projectId: number | null;
  templateRunId: number | null;
  sourceMethod: RunSchedulerSourceMethod;
  watchSourcePath: string;
  amazonCredentialId: number;
  googleCredentialId: number;
  sampleTrackerId: string | null;
  collectionId: string | null;
  watchPathField: string | null;
  statusField: string | null;
  readyValue: string;
  runConfigText: string;
  markerFileRelativePath: string;
  startDate: string;
  endDate: string;
  intervalMinutes: number;
  overwriteRows: RunSchedulerOverwriteParameter[];
}

export function buildSchedulerPayload(values: SchedulerFormValues) {
  let runConfigJson: unknown = values.runConfigText;
  try {
    runConfigJson = JSON.parse(values.runConfigText);
  } catch {
    // allow non-JSON; backend can validate
  }

  const sm = values.sourceMethod;
  const normalizedWatchSourcePath = values.watchSourcePath.trim();
  const isS3Path = normalizedWatchSourcePath.startsWith("s3://");
  const isGsPath = normalizedWatchSourcePath.startsWith("gs://");

  return {
    name: values.name.trim(),
    projectId: Number(values.projectId),
    enabled: values.enabled,
    templateRunId: Number(values.templateRunId),
    sourceMethod: sm,
    ...(sm === "watch-path"
      ? {
          watchPathSource: normalizedWatchSourcePath,
          ...(isS3Path &&
          values.amazonCredentialId !== BUILT_IN_ACCESS_OPTION_ID
            ? { amazonCreId: values.amazonCredentialId }
            : {}),
          ...(isGsPath &&
          values.googleCredentialId !== BUILT_IN_ACCESS_OPTION_ID
            ? { googleCreId: values.googleCredentialId }
            : {}),
        }
      : {
          sampleTrackerId: String(values.sampleTrackerId),
          sampleTrackerCollectionId: String(values.collectionId),
          watchPathField: String(values.watchPathField),
          statusField: String(values.statusField),
          readyValue: values.readyValue.trim(),
        }),
    markerFileRelativePath:
      sm === "watch-path"
        ? values.markerFileRelativePath.trim() || undefined
        : undefined,
    startDate: values.startDate
      ? new Date(values.startDate).toISOString()
      : undefined,
    endDate: values.endDate
      ? new Date(values.endDate).toISOString()
      : undefined,
    intervalMinutes: values.intervalMinutes,
    runConfigJson,
    overwriteParameters:
      values.overwriteRows.length === 0
        ? undefined
        : values.overwriteRows.map(r => ({
            configParameter: r.configParameter.trim(),
            method:
              sm === "sample-tracker"
                ? r.method === "append-suffix"
                  ? "replace"
                  : (r.method ?? "replace")
                : (r.method ?? "replace"),
            valueType:
              sm === "sample-tracker" ? "sample-tracker-field" : r.valueType,
            ...(sm === "sample-tracker" ||
            r.valueType === "sample-tracker-field"
              ? { sampleTrackerField: r.sampleTrackerField?.trim() }
              : {}),
            ...((r.method ?? "replace") === "save-as-dataset"
              ? {
                  samplesheetTargetCanvasId:
                    r.samplesheetTargetCanvasId?.trim(),
                  ...(sm === "sample-tracker"
                    ? {
                        samplesheetFileLocation:
                          r.samplesheetFileLocation ?? "run-environment",
                        samplesheetDatasetNameField:
                          r.samplesheetDatasetNameField?.trim(),
                        ...(r.samplesheetFileLocation === "s3" &&
                        Number(r.samplesheetAmazonCreId) !==
                          BUILT_IN_ACCESS_OPTION_ID
                          ? {
                              samplesheetAmazonCreId: Number(
                                r.samplesheetAmazonCreId
                              ),
                            }
                          : {}),
                        ...(r.samplesheetFileLocation === "gcs" &&
                        Number(r.samplesheetGoogleCreId) !==
                          BUILT_IN_ACCESS_OPTION_ID
                          ? {
                              samplesheetGoogleCreId: Number(
                                r.samplesheetGoogleCreId
                              ),
                            }
                          : {}),
                      }
                    : {}),
                }
              : {}),
            ...(sm !== "sample-tracker" && r.valueType === "relative-path"
              ? { relativePath: r.relativePath?.trim() }
              : {}),
            ...(sm !== "sample-tracker" &&
            r.valueType === "json-key-from-relative-path"
              ? {
                  jsonFileRelativePath: r.jsonFileRelativePath?.trim(),
                  jsonKeyDotPath: r.jsonKeyDotPath?.trim(),
                }
              : {}),
            ...(sm !== "sample-tracker" &&
            r.valueType === "derive-from-source-path"
              ? { sourcePathDerivation: r.sourcePathDerivation }
              : {}),
          })),
  };
}
